import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowUpRight, RefreshCcw, Route as RouteIcon, Users, FileText, Activity as ActivityIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, relativeFr, formatDate } from "@/lib/utils";
import { fmtDateFr, relAgo, daysUntil } from "./placementsShared";
import { useLang, translateFr as tfr } from "@/i18n";

export function Card({ title, Icon, children, testid, action }) {
  return (
    <section className="min-w-0 rounded-2xl border border-border dark:border-border bg-card p-4 md:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <h4 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">{title}</h4>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </section>
  );
}

export function Row({ label, value, tone }) {
  const { tf } = useLang();
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className={cn("font-medium text-right min-w-0 break-words", tone, (value === null || value === undefined || value === "") && "text-muted-foreground/50 font-normal")}>
        {value === null || value === undefined || value === "" ? tf("Non renseigné") : value}
      </span>
    </div>
  );
}

export function ContractTab({ detail }) {
  const { tf } = useLang();
  const c = detail.contract;
  const r = detail.renewal;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card title={tf("Contrat lié")} Icon={ScrollText} testid="placement-contract-card">
        {c ? (
          <>
            <Row label={tf("Titre")} value={c.title} />
            <Row label={tf("Statut")} value={c.status} />
            <Row label={tf("Contrepartie")} value={c.counterparty} />
            <Row label={tf("Début")} value={c.start_date ? fmtDateFr(c.start_date) : null} />
            <Row label={tf("Fin")} value={c.end_date ? fmtDateFr(c.end_date) : null} />
            {c.days_left != null && c.days_left >= 0 && <Row label={tf("Temps restant")} value={`${c.days_left} ${tf("jours")}`} />}
            <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
              <Link to={`/contracts/${c.id}`} data-testid="open-contract-link">
                {tf("Ouvrir le contrat")} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="contract-empty">
            {tf("Aucun contrat n'est lié à ce placement.")}
          </p>
        )}
      </Card>
      <Card title={tf("Renouvellement")} Icon={RefreshCcw} testid="placement-renewal-card">
        {r ? (
          <>
            <Row label={tf("Phase actuelle")} value={r.status_label || r.status} />
            {r.contract_end_date && <Row label={tf("Contrat jusqu'au")} value={fmtDateFr(r.contract_end_date)} />}
            {r.days_left != null && <Row label={tf("Jours restants")} value={`${r.days_left}`} tone={r.days_left <= 90 ? "text-warning" : undefined} />}
            {r.priority?.label && <Row label={tf("Priorité")} value={r.priority.label} />}
            {r.next_action?.title && <Row label={tf("Prochaine action")} value={r.next_action.title} />}
            <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
              <Link to={`/contracts/renewals/${r.id}`} data-testid="open-renewal-link">
                {tf("Voir dans Renouvellements")} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="renewal-empty">
            {c ? tf("Ce contrat n'est pas encore entré dans sa fenêtre de renouvellement.") : tf("Le renouvellement n'est pas encore ouvert.")}
          </p>
        )}
      </Card>
    </div>
  );
}

export function CareerTab({ detail }) {
  const { tf } = useLang();
  const goals = detail.career_goals || [];
  const pid = detail.athlete?.id;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card title={tf("Objectif du placement")} Icon={RouteIcon} testid="career-objective-card">
        <p className={cn("text-sm leading-relaxed", !detail.objective && "text-muted-foreground/60")}>
          {detail.objective ? `« ${detail.objective} »` : tf("Non renseigné")}
        </p>
      </Card>
      <Card title={tf("Objectifs de carrière actifs")} Icon={RouteIcon} testid="career-goals-card">
        {goals.length === 0 ? (
          <p className="text-sm text-muted-foreground">{tf("Aucun objectif de carrière actif.")}</p>
        ) : (
          <ul className="space-y-2">
            {goals.map((g) => (
              <li key={g.id} className="text-sm flex items-start justify-between gap-2">
                <span>{g.title}</span>
                {g.due_date && <span className="text-xs text-muted-foreground shrink-0">{fmtDateFr(g.due_date)}</span>}
              </li>
            ))}
          </ul>
        )}
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
          <Link to={`/players/${pid}/career-plan`} data-testid="open-career-link">
            {tf("Ouvrir le plan de carrière")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </Card>
    </div>
  );
}

export function RelationTab({ detail }) {
  const { tf } = useLang();
  const rel = detail.relation || {};
  const pid = detail.athlete?.id;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card title={tf("Relation avec l'athlète")} Icon={Users} testid="relation-athlete-card">
        <Row label={tf("Dernier échange")} value={rel.last_interaction_at ? `${relAgo(rel.last_interaction_at)} · ${fmtDateFr(rel.last_interaction_at)}` : null} />
        <Row label={tf("Interactions (90 jours)")} value={`${rel.interactions_90d ?? 0}`} />
        <Row label={tf("Relances ouvertes")} value={`${rel.open_reminders ?? 0}`} />
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
          <Link to={`/players/${pid}`} data-testid="open-athlete-link">
            {tf("Ouvrir la fiche athlète")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </Card>
      <Card title={tf("Relation avec le club")} Icon={Users} testid="relation-club-card">
        <Row label={tf("Club")} value={detail.club || detail.athlete?.club} />
        <Row label={tf("Statut de la relation")} value={rel.club_relationship_status} />
        {rel.club_id && (
          <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
            <Link to={`/clubs/${rel.club_id}`} data-testid="open-club-link">
              {tf("Voir le club")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
      </Card>
    </div>
  );
}

export function DocumentsTab({ detail }) {
  const { tf } = useLang();
  const docs = detail.documents || [];
  const pid = detail.athlete?.id;
  return (
    <Card title={tf("Documents de l'athlète")} Icon={FileText} testid="placement-docs-card">
      {docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucun document.")}</p>
      ) : (
        <ul className="divide-y divide-border dark:divide-border/60">
          {docs.slice(0, 12).map((d) => {
            const dl = d.expires_at ? daysUntil(d.expires_at) : null;
            return (
              <li key={d.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <span className="truncate">{d.label || d.doc_type}</span>
                {d.expires_at && (
                  <span className={cn("text-xs shrink-0", dl != null && dl < 0 ? "text-athlynx" : dl != null && dl <= 30 ? "text-warning" : "text-muted-foreground")}>
                    {dl != null && dl < 0 ? tf("Expiré") : `${tf("Expire le")} ${fmtDateFr(d.expires_at)}`}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
        <Link to={`/players/${pid}`} data-testid="manage-docs-link">
          {tf("Gérer les documents")} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </Card>
  );
}

const ACTIVITY_LABELS = {
  PLACEMENT_CREATED: tfr("Placement confirmé"),
  PLACEMENT_UPDATED: tfr("Placement modifié"),
  PLACEMENT_CLOSED: tfr("Placement clôturé"),
  FOLLOW_UP_COMPLETED: tfr("Relance terminée"),
  FOLLOW_UP_CREATED: tfr("Relance planifiée"),
  PLACEMENT_FOLLOWUP_CREATED: tfr("Suivi planifié"),
  PLACEMENT_FOLLOWUP_COMPLETED: tfr("Suivi réalisé"),
  PLACEMENT_FOLLOWUP_UPDATED: tfr("Suivi modifié"),
  PLAYER_STAGE_CHANGED: tfr("Changement de statut"),
  EVENT_CREATED: tfr("Événement créé"),
};

export function ActivityTab({ detail, onAddInteraction }) {
  const { tf } = useLang();
  const acts = detail.activity || [];
  return (
    <Card title={tf("Activité récente")} Icon={ActivityIcon} testid="placement-activity-card"
      action={detail.status === "ACTIVE" && onAddInteraction && (
        <button onClick={onAddInteraction} className="text-[11px] text-info hover:underline" data-testid="add-interaction-btn">
          + {tf("Ajouter une interaction")}
        </button>)}>
      {acts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucune activité enregistrée.")}</p>
      ) : (
        <ul className="divide-y divide-border dark:divide-border/60">
          {acts.map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0 break-words">
                <span className="font-medium">{tf(ACTIVITY_LABELS[a.action_type] || a.action_type)}</span>
                {a.metadata?.label && <span className="text-muted-foreground"> · {a.metadata.label}</span>}
                {a.metadata?.to && <span className="text-muted-foreground"> · {a.metadata.from ? `${a.metadata.from} → ` : ""}{a.metadata.to}</span>}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{relativeFr(a.created_at) || formatDate(a.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
